import { HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { RealTime_Hub } from "../API/axios";
import { requireRealtimeAuth } from "../utils/jwtUtils";

let connection = null;
let startingPromise = null;
const handlers = new Map();

const HUB_EVENTS = [
  "ReplyChat",
  "DeleteChat",
  "ReadChat",
  "ReadAllChat",
  "RealTimeNotification",
];

const dispatch = (eventName, ...args) => {
  if (eventName === "ReadChat" || eventName === "ReadAllChat") {
    console.warn(`[ReadReceipt] HUB event "${eventName}"`, args[0]);
  }

  const callbacks = handlers.get(eventName);
  if (!callbacks?.size) {
    if (eventName === "ReadChat" || eventName === "ReadAllChat") {
      console.warn(
        `[ReadReceipt] No handler registered for "${eventName}" — event dropped`
      );
    }
    return;
  }

  callbacks.forEach((callback) => {
    try {
      callback(...args);
    } catch (error) {
      console.error(`Realtime hub handler failed for ${eventName}:`, error);
    }
  });
};

const attachConnectionHandlers = () => {
  if (!connection) return;

  HUB_EVENTS.forEach((eventName) => {
    connection.off(eventName);
    connection.on(eventName, (...args) => dispatch(eventName, ...args));
  });
};

const buildConnection = () => {
  connection = new HubConnectionBuilder()
    .withUrl(RealTime_Hub, {
      accessTokenFactory: () => localStorage.getItem("token") || "",
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .build();

  attachConnectionHandlers();
};

export const onHubEvent = (eventName, callback) => {
  if (!handlers.has(eventName)) {
    handlers.set(eventName, new Set());
  }

  handlers.get(eventName).add(callback);

  return () => {
    handlers.get(eventName)?.delete(callback);
  };
};

export const startRealtimeHub = async () => {
  if (!requireRealtimeAuth()) {
    return null;
  }

  if (connection?.state === HubConnectionState.Connected) {
    return connection;
  }

  if (startingPromise) {
    return startingPromise;
  }

  startingPromise = (async () => {
    if (
      !connection ||
      connection.state === HubConnectionState.Disconnected
    ) {
      if (connection) {
        try {
          await connection.stop();
        } catch {
          // ignore stop errors while rebuilding
        }
      }
      buildConnection();
    }

    try {
      await connection.start();
      console.warn("[ReadReceipt] SignalR connected");
    } catch (error) {
      console.log("Realtime hub connection failed:", error);
      connection = null;
      throw error;
    }

    return connection;
  })();

  try {
    return await startingPromise;
  } finally {
    startingPromise = null;
  }
};

export const stopRealtimeHub = async () => {
  startingPromise = null;

  if (!connection) return;

  try {
    await connection.stop();
  } catch {
    // ignore
  } finally {
    connection = null;
  }
};

export const getRealtimeHubState = () => connection?.state ?? HubConnectionState.Disconnected;
